import { locationChanged } from '@lp/features/forms/CreateCaseForm/utils';
import { getTxAssigneesDiff } from '@features/components/CaseFormBlocks/utils';
import { CaseTxCenterFormType, txCenterDefaultValues, txCenterFormValues } from './config';
import { Case, TxCenterORData } from '@/api/apis/case';
import { getChangedValues } from '@/utils/helpers';

export const checkShowTxCenterORChangeReason = (formData: Partial<CaseTxCenterFormType>, caseData: Case): boolean => {
  const defaultValues = txCenterFormValues(caseData);

  const previouslyHadDateTime = Boolean(
    defaultValues.recipientHospitalDate &&
      defaultValues.recipientHospitalTime &&
      defaultValues.recipientHospitalTimeZone,
  );

  const valuesChanged =
    formData.recipientHospitalDate !== defaultValues.recipientHospitalDate ||
    formData.recipientHospitalTime !== defaultValues.recipientHospitalTime ||
    formData.recipientHospitalTimeZone !== defaultValues.recipientHospitalTimeZone;

  const txCenterChanged = formData.txCenterId !== defaultValues.txCenterId;

  if (txCenterChanged) return false;

  if (defaultValues.recipientHospitalORChangeReason) return true;

  return previouslyHadDateTime && valuesChanged;
};

export const validateFormSubmit = (data: unknown, caseData: Case) => {
  const formData = data as CaseTxCenterFormType;
  const errors: Record<string, { message: string }> = {};

  const showTxCenterORChangeReason = checkShowTxCenterORChangeReason(formData, caseData);
  const defaultValues = txCenterFormValues(caseData);

  const valuesChanged =
    formData.recipientHospitalDate !== defaultValues.recipientHospitalDate ||
    formData.recipientHospitalTime !== defaultValues.recipientHospitalTime ||
    formData.recipientHospitalTimeZone !== defaultValues.recipientHospitalTimeZone;

  if (showTxCenterORChangeReason && !formData.recipientHospitalORChangeReason?.length && valuesChanged) {
    errors.recipientHospitalORChangeReason = {
      message:
        'You are about to update the TX Center OR. Please provide a brief explanation (max length up to 128 characters)',
    };
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

export const prepareTxCenterSubmissionData = (formData: CaseTxCenterFormType, caseData: Case) => {
  const {
    txCenterId,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    recipientHospitalORChangeReason,
    txCenterCustomLocation,
    coordinatorId,
    receivingSurgeonId,
    isServiceWithoutCoordinator,
    opoRequest,
  } = formData;

  const newFormValues = {
    txCenterId,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    recipientHospitalORChangeReason,
    coordinatorId,
    receivingSurgeonId,
    isServiceWithoutCoordinator,
    opoRequest,
  };

  const prevFormValues = txCenterDefaultValues(caseData);
  const {
    coordinatorId: updatedCoordinatorId,
    receivingSurgeonId: newSurgeonId,
    ...changedValues
  } = getChangedValues<Partial<CaseTxCenterFormType>>(newFormValues, prevFormValues);

  if (locationChanged(prevFormValues?.txCenterCustomLocation, txCenterCustomLocation)) {
    changedValues.txCenterCustomLocation = txCenterCustomLocation;
  }

  const defaultTxAssignees = getTxAssigneesDiff(prevFormValues, newFormValues);

  const valuesToPatch = { ...changedValues, ...(defaultTxAssignees.length ? { defaultTxAssignees } : {}) };

  const orFieldKeys = [
    'recipientHospitalDate',
    'recipientHospitalTime',
    'recipientHospitalTimeZone',
    'recipientHospitalORChangeReason',
  ] as const;

  const hasOrChanges = orFieldKeys.some((key) => valuesToPatch[key] !== undefined);

  const orFields = orFieldKeys.reduce((acc, key) => {
    acc[key] = hasOrChanges ? (valuesToPatch[key] ?? formData[key] ?? null) : null;
    return acc;
  }, {} as TxCenterORData);

  const otherValuesToPatch = Object.fromEntries(
    Object.entries(valuesToPatch).filter(([key]) => !orFieldKeys.includes(key as (typeof orFieldKeys)[number])),
  );

  return { orFields, otherValuesToPatch };
};
